import { alpha, Theme } from "@mui/material";
import Button from "@mui/material/Button";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import React, { forwardRef, RefAttributes } from "react";

interface Props {
    title: string;
    onClicked: () => void;

    disabled?: boolean;
    iconLeft?: React.ReactNode;
    testId?: string;

    noMinWidth?: boolean;
    className?: string;
    light?: boolean;
    size?: "tiny" | "small" | "medium" | "large";
}

const useStyles = makeStyles((theme: Theme) => ({
    button: {
        whiteSpace: "nowrap",
        fontFamily: theme.typography.button.fontFamily,
        fontWeight: theme.typography.button.fontWeight,
        textTransform: "none",
        padding: "8px 24px",
        transition: theme.transitions.create(["border", "color", "background-color"]),
        minWidth: "144px",
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
        },
        "&:disabled": {
            color: alpha(theme.palette.primary.contrastText, 0.54)
        }
    },
    buttonLight: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.contrastText,
        "&:hover": {
            backgroundColor: alpha(theme.palette.primary.contrastText, 0.8),
        },
        "&:disabled": {
            color: alpha(theme.palette.primary.main, 0.54)
        }
    },
    tiny: {
        padding: "2px 8px",
        minWidth: 0
    },
    small: {
        // Moves the text to align better with a possible icon
        padding: "5px 12px 4px 12px",
        minWidth: "100px"
    },
    large: {
        padding: "12px 36px",
        minWidth: "168px"
    },
    noMinWidth: {
        minWidth: "auto !important"
    }
}));

const QAButton: React.FC<Props & RefAttributes<HTMLButtonElement>> = forwardRef((props, ref) => {
    const classes = useStyles();
    return (
        <Button
            ref={ref}
            data-cy={props.testId}
            disableElevation
            className={clsx(classes.button, props.light && classes.buttonLight, {
                [classes.tiny]: props.size === "tiny",
                [classes.small]: props.size === "small",
                [classes.large]: props.size === "large",
                [classes.noMinWidth]: props.noMinWidth
            }, props.className)}
            onClick={props.onClicked}
            startIcon={props.iconLeft}
            variant="outlined"
            size={props.size === "tiny" ? "small" : (props.size ?? "medium")}
            disabled={props.disabled}>
            {props.title}
        </Button>
    );
});

export default QAButton;
